import { Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import {
  ApproveUser,
  ApproveUserSuccess,
  ApproveUserFailed,
  RejectUser,
  RejectUserSuccess,
  RejectUserFailed,
  LoadExternalManagementState,
  LoadExternalManagementStateSuccess,
  LoadExternalManagementStateFail,
  ExternalManagementActionTypes,
  AddExceptionEIN,
  AddExceptionEINSuccess,
  AddExceptionEINFail,
  CharityCheck,
  CharityCheckSuccess,
  CharityCheckFail,
} from './external-users-management.actions';
import { LGEDataService, IExternalData } from '../../../commons';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, from, map, of, switchMap } from 'rxjs';

@Injectable()
export class ExternalUsersManagementEffects {
  externalData: IExternalData;
  isApproving = false;

  loadExternalManagementState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExternalManagementActionTypes.LoadExternalManagementState),
      switchMap(() =>
        from(this._lgeDataService.getExternalUsers()).pipe(
          map(
            (response: IExternalData[]) =>
              new LoadExternalManagementStateSuccess(response)
          ),
          catchError((error) => of(new LoadExternalManagementStateFail(error)))
        )
      )
    )
  );

  addExceptionEIN$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExternalManagementActionTypes.AddExceptionEINState),
      map((action: AddExceptionEIN) => [
        action.externalData,
        action.isApproving,
      ]),
      switchMap((res:any) => {
        this.externalData = res[0];
        this.isApproving = res[1];
        let einOrgInfo: any = {};
        einOrgInfo.ein = this.externalData.orgInfoExt.EIN;
        einOrgInfo.organization_name = this.externalData.orgInfoExt.name;
        einOrgInfo.address_line1 = this.externalData.orgInfoExt.address1;
        einOrgInfo.address_line2 = this.externalData.orgInfoExt.address2;
        einOrgInfo.city = this.externalData.orgInfoExt.city;
        einOrgInfo.state = this.externalData.orgInfoExt.state;
        einOrgInfo.zip = this.externalData.orgInfoExt.zipCode;
        einOrgInfo.isException = 1;
        einOrgInfo.isVerified = 1;
        einOrgInfo.lastUpdated = new Date();
        einOrgInfo.einOrgInfoId = null;
        einOrgInfo.subsection_501 = this.externalData.orgInfoExt.subsection_501;
        einOrgInfo.deleteIt = true;
        return this._lgeDataService.EINOrgInfoDetailIUD(einOrgInfo);
      }),
      switchMap(() => {
        if (this.isApproving) {
          let approvedUser: IExternalData = {
            orgInfoExt: this.externalData.orgInfoExt,
            orgContactInfoExt: this.externalData.orgContactInfoExt,
            externalUser: Object.assign({}, this.externalData.externalUser, {
              accessStatus:
                this.externalData.externalUser.accessStatus === 'pendingEIN'
                  ? 'approvedEIN'
                  : 'approved',
              isVerified: 1,
            }),
          };
          return [new ApproveUser(approvedUser)];
        } else
          return [
            new AddExceptionEINSuccess('Success'),
            new LoadExternalManagementState(),
          ];
      }),
      catchError((error) => of(new AddExceptionEINFail(error)))
    )
  );

  charityCheck$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExternalManagementActionTypes.CharityCheckState),
      map((action: CharityCheck) => action.externalData),
      switchMap((externalData: IExternalData) => {
        this.externalData = externalData;
        return this._lgeDataService.CharityCheck(
          this.externalData.orgInfoExt.EIN
        );
      }),
      switchMap((res) => {
        if (res === 'invalid')
          return [new AddExceptionEIN(this.externalData, false)];
        else return [new LoadExternalManagementState()];
      }),
      catchError((error) => of(new CharityCheckFail(error)))
    )
  );

  approveUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExternalManagementActionTypes.ApproveUser),
      map((action: ApproveUser) => action.payload),
      switchMap((externalUser: IExternalData) =>
        this._lgeDataService.externalProfileApproveReject(externalUser)
      ),
      switchMap(() => [
        new ApproveUserSuccess('approved'),
        new LoadExternalManagementState(),
      ]),
      catchError((error) => of(new ApproveUserFailed(error)))
    )
  );
  denyUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExternalManagementActionTypes.RejectUser),
      map((action: RejectUser) => action.payload),
      switchMap((externalUser: IExternalData) =>
        this._lgeDataService.externalProfileApproveReject(externalUser)
      ),
      switchMap(() => [
        new RejectUserSuccess('rejected'),
        new LoadExternalManagementState(),
      ]),
      catchError((error) => of(new RejectUserFailed(error)))
    )
  );

  constructor(
    private actions$: Actions,
    private _lgeDataService: LGEDataService
  ) {}
}
